import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { CommittedRowSchema } from '../../shared/bulk-commit.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { optionalEmailSchema } from '../../shared/email.schema';
/**
 * Bulk Add Department Input Schema
 * Input format for adding departments in bulk with hierarchical relationships
 */ export const BulkAddDepartmentInputSchema = z.strictObject({
    name: z.string(),
    description: z.string().optional(),
    abbreviation: z.string(),
    // parent: z.string().optional(), // Parent department ObjectId (for parents in DB)
    parentAbbr: z.string().optional(),
    // Absent or '': saved without a manager hint, offen in Manager auflösen.
    managerEmail: optionalEmailSchema()
});
/**
 * Update Department Input Schema
 * In-place edit of a wizard-seeded department, matched by `_id`
 * (ADR 0011, second amendment)
 */ export const UpdateDepartmentInputSchema = BulkAddDepartmentInputSchema.safeExtend({
    _id: ObjectIdSchema
});
/**
 * Add Departments Bulk Request Schema
 * POST /api/v2/initialize/departments
 * Allows bulk creation of departments with hierarchical relationships
 */ export const InitializeAddDepartmentsRequestSchema = z.object({
    departments: z.array(BulkAddDepartmentInputSchema),
    // In-place edits of already-seeded rows, matched by `_id`. Non-destructive:
    // later steps keep their progress (ADR 0011, second amendment).
    updates: z.array(UpdateDepartmentInputSchema).optional(),
    // Surgical deletion of already-seeded rows by `_id`. Children reparent to
    // the removed department's parent; optional references are cleared,
    // required ones block the removal (ADR 0011, third amendment).
    removals: z.array(ObjectIdSchema).optional()
}).refine((v)=>v.departments.length > 0 || !!v.updates?.length || !!v.removals?.length, {
    message: 'at least one of departments, updates or removals is required',
    path: [
        'departments'
    ]
}).refine((v)=>!v.updates?.some((update)=>v.removals?.includes(update._id)), {
    message: 'an id cannot appear in both updates and removals',
    path: [
        'removals'
    ]
});
/**
 * Add Departments Bulk Response Schema
 * One result per request `departments` row, in request order. Departments
 * never skip (a duplicate name/abbreviation is a validation error), so every
 * row reports `created: true`.
 */ export const InitializeAddDepartmentsResponseSchema = z.object({
    departments: z.array(CommittedRowSchema)
});
/**
 * Add Departments Bulk Error Schema
 * Returned when department creation fails
 */ export const InitializeAddDepartmentsErrorSchema = BaseErrorSchema;

//# sourceMappingURL=initialize-add-departments.schema.js.map